import React, { CSSProperties, useEffect, useId, useState } from 'react';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useSupportsHover } from 'hooks/useSupportsHover';

import { BottomSheet, IconDisc } from 'components/BottomSheet';
import GameModal from 'components/GameModal/GameModal';

type Props = {
  open: boolean;
  tournament: any | null;
  onClose: () => void;
  onJoin: () => void;
};

const URGENT_THRESHOLD_SECONDS = 60;

const TournamentAboutToStartModal = ({ open, tournament, onClose, onJoin }: Props) => {
  // Tick every second while open so the message text and the 60-second urgent
  // cross-over both stay in sync without any parent re-render.
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    if (!open) return;
    setNow(Math.floor(Date.now() / 1000));
    const id = window.setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => window.clearInterval(id);
  }, [open]);

  // Touch-vs-pointer decides the surface, NOT native-vs-web: the bottom
  // drawer's swipe/tap-outside gesture belongs anywhere a finger is driving
  // the UI (Capacitor iOS/Android app AND mobile browser both), while a
  // desktop pointer has no swipe gesture and reads the centered card the
  // same as every other in-lobby modal on that surface.
  const supportsHover = useSupportsHover();
  const useDrawer = !supportsHover;
  const titleId = useId();

  if (!tournament) return null;

  const startAt = Number(tournament.startAtTime || 0);
  const isSng = tournament.gameType === 'SitAndGo';
  const secondsLeft = Math.max(0, startAt - now);
  // Once the tournament's status flips to 'P' (In Progress) or the start
  // timestamp has elapsed, the "about to start" framing is stale — re-skin the
  // modal as a "has started" prompt so the player understands why they're
  // still seeing it and can jump in immediately.
  const hasStarted = tournament.status === 'P' || (!isSng && startAt > 0 && secondsLeft === 0);
  const isUrgent = hasStarted || isSng || secondsLeft < URGENT_THRESHOLD_SECONDS;

  const tableName = tournament.tablename || 'tournament';

  let title: string;
  let message: string;
  if (hasStarted) {
    // "The tournament / Sit & Go has started!" — reads as an event notification,
    // not a possessive statement about the player's own tournament (per spec).
    title = isSng ? 'The Sit & Go has started!' : 'The tournament has started!';
    message = 'Tap Join Now to take your seat.';
  } else if (isSng) {
    title = 'Your Sit & Go is ready!';
    message = 'Enough players have joined. Your game is about to begin!';
  } else if (isUrgent) {
    title = 'Tournament is about to start!';
    message = 'The tournament will start in less than a minute.';
  } else {
    const minutes = Math.max(1, Math.ceil(secondsLeft / 60));
    title = 'Tournament is about to start!';
    message = `The tournament will start in ${minutes} minute${minutes === 1 ? '' : 's'}.`;
  }

  // "Go to Tournament" the entire time the prompt reads "about to start" or
  // "ready" — the player is being invited to head over, not to join right now.
  // Only after the tournament has actually STARTED does the CTA shift to the
  // more urgent "Join Now", which pairs with the "Tap Join Now to take your
  // seat." body copy.
  const primaryLabel = hasStarted ? 'Join Now' : 'Go to Tournament';

  // Shared content — same biometric-family visual language on both surfaces
  // (native bottom drawer AND web centered card), so a player sees the same
  // prompt with the same rhythm regardless of where they are.
  const content = (
    <>
      {/* Hollow-circle biometric-prompt disc. Swings and glows once the
          tournament has actually started so the player's eye lands on the
          CTA at the transition. Glyph is the same warm orange as the disc's
          border so the icon reads as part of the disc, not a token pasted
          inside it. */}
      <IconDisc
        active={hasStarted}
        animation={hasStarted ? 'bell-shake' : 'none'}
        icon={<NotificationsActiveIcon style={{ fontSize: 32, color: '#ffb45c' }} />}
      />
      {/* Copy block — title tight above subtitle above message, matching the
          registration/biometric flow's icon → heading → body rhythm. */}
      <div style={copyBlockStyle}>
        <div id={titleId} style={headingStyle}>
          {title}
        </div>
        <div style={subtitleStyle}>
          You are registered for the <strong style={{ color: '#f1e3c7' }}>{tableName}</strong>
        </div>
        <div style={messageStyle}>{message}</div>
      </div>
      {/* Actions — biometric-family primary button (orange gradient, dark
          Oswald label) with a text-only secondary beneath. Cancel is
          present in every state, per spec. */}
      <div style={actionsStackStyle}>
        <button
          type='button'
          style={primaryButtonStyle}
          onClick={onJoin}
          onMouseDown={(e) => (e.currentTarget.style.filter = 'brightness(0.95)')}
          onMouseUp={(e) => (e.currentTarget.style.filter = 'none')}
          onMouseLeave={(e) => (e.currentTarget.style.filter = 'none')}>
          {primaryLabel}
        </button>
        <button type='button' style={secondaryButtonStyle} onClick={onClose}>
          Cancel
        </button>
      </div>
    </>
  );

  // Touch surface (native app OR mobile browser): swipe/tap bottom drawer
  // with the blurred backdrop. Pointer surface (desktop): the standard
  // centered GameModal card so the prompt reads like every other in-lobby
  // modal on that surface.
  if (useDrawer) {
    return (
      <BottomSheet
        open={open}
        onClose={onClose}
        testId='tournament-drawer-backdrop'
        ariaLabelledBy={titleId}>
        {content}
      </BottomSheet>
    );
  }
  return (
    <GameModal
      open={open}
      onClose={onClose}
      maxWidth='420px'
      showClose={false}
      closeOnEscape
      ariaLabelledBy={titleId}>
      {content}
    </GameModal>
  );
};

export default TournamentAboutToStartModal;

// ---------------------------------------------------------------------------
// Styles — biometric-prompt family (lifted from d4Primitives' Heading /
// BodyText / PrimaryButton / NotNowButton and their `disc` + `primaryBtn` +
// `notNow` tokens in d4Tokens.ts, Hijack skin). Every value here is a
// deliberate mirror so this prompt and the registration/biometric flow
// read as one system on BOTH surfaces (drawer + centered card).
// ---------------------------------------------------------------------------

// Copy block wrapper — a small internal gap between title / subtitle /
// message so the three lines read as one unit and the outer shell's own
// padding keeps them separated from the icon and the actions.
const copyBlockStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.35rem'
};

// Matches d4Primitives' Heading (Hijack skin) — Inter 700 19px.
const headingStyle: CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontWeight: 700,
  fontSize: 19,
  letterSpacing: '0.01em',
  lineHeight: 1.18,
  color: '#f1e3c7',
  textAlign: 'center'
};

// Matches d4Primitives' BodyText — Inter 12.5px, warm-bone muted.
const subtitleStyle: CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: 12.5,
  lineHeight: 1.55,
  color: 'rgba(217, 199, 158, 0.72)',
  textAlign: 'center'
};

const messageStyle: CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: 12.5,
  lineHeight: 1.55,
  color: 'rgba(217, 199, 158, 0.72)',
  textAlign: 'center'
};

// Actions vertical stack — same gap as d4's action rows so the two buttons
// don't crowd each other AND the sheet's own bottom padding gives the
// secondary its "not now" breathing room.
const actionsStackStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: '0.5rem',
  width: '100%'
};

// Matches d4Primitives' PrimaryButton — Oswald 14 uppercase, DARK label on
// the warm orange gradient (the reference's ENABLE FACE ID button).
const primaryButtonStyle: CSSProperties = {
  width: '100%',
  border: 0,
  borderRadius: 8,
  padding: 12,
  minHeight: 48,
  cursor: 'pointer',
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 600,
  fontSize: 14,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#1a0e06',
  background: 'linear-gradient(180deg, #ffb45c, #e8821f 60%, #b85f12)',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 220, 160, 0.4)',
  transition: 'filter 0.15s ease'
};

// Matches d4Primitives' NotNowButton — a text-only affordance sitting under
// the primary. No background, no border, just Oswald uppercase in muted tan.
const secondaryButtonStyle: CSSProperties = {
  width: '100%',
  background: 'transparent',
  border: 0,
  padding: 13,
  minHeight: 44,
  cursor: 'pointer',
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 500,
  fontSize: 13,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#d9c79e'
};
